/**
 * Config del carrusel de imágenes: a diferencia de imageDensity, no tiene posiciones manuales
 * ni edición de ejes, solo qué columnas del resultado SQL traen sensor_type, sensor_id y el
 * content (JSON) de la detección, más las opciones visuales del gráfico (una imagen a la vez,
 * recorte por detección, filtro por clase, mostrar solo dentro de zona).
 */

export const DEFAULT_IMAGE_CAROUSEL = {
  sensorTypeColumn: 'sensor_type',
  sensorIdColumn: 'sensor_id',
  contentColumn: 'content',
  singleImageMode: true,
  // Columna booleana opcional que marca si la fila cae dentro de la zona configurada.
  // La resolución real de la zona (polígono del sensor) queda pendiente.
  inZoneColumn: '',
  showOnlyInZone: false,
  // Recorte por detección (igual que la ficha de patentes de VSaaS): en vez de la imagen
  // completa con cajas superpuestas, una miniatura recortada por cada detección.
  cropMode: false,
  // Clase opcional (ej. "license_plate") para quedarse solo con esas detecciones; ver
  // filterSlidesByClass en imageCarouselSlides.js para el porqué.
  cropClassFilter: '',
};

function vizFieldsToImageCarouselConfig(viz = {}) {
  return {
    sensorTypeColumn: viz.carouselSensorTypeColumn || 'sensor_type',
    sensorIdColumn: viz.carouselSensorIdColumn || 'sensor_id',
    contentColumn: viz.carouselContentColumn || 'content',
    singleImageMode: viz.carouselSingleImageMode === true,
    inZoneColumn: viz.carouselInZoneColumn || '',
    showOnlyInZone: viz.carouselShowOnlyInZone === true,
    cropMode: viz.carouselCropMode === true,
    cropClassFilter: viz.carouselCropClassFilter || '',
  };
}

/**
 * Mapea nombres lógicos (sensor_type) a claves reales del result set del chart builder
 * (sensor_type_sensor_dataset_items_enriched), que usa makeAlias/disambiguateAliases en SQL.
 */
export function resolveImageCarouselColumnKeys(cfg = {}, sampleRow) {
  if (!sampleRow || typeof sampleRow !== 'object') return { ...cfg };
  const rowKeys = Object.keys(sampleRow);
  const resolveKey = (logical) => {
    const name = String(logical || '').trim();
    if (!name) return name;
    if (Object.prototype.hasOwnProperty.call(sampleRow, name)) return name;
    const match = rowKeys.find((k) => k === name || k.startsWith(`${name}_`));
    return match || name;
  };
  return {
    ...cfg,
    sensorTypeColumn: resolveKey(cfg.sensorTypeColumn),
    sensorIdColumn: resolveKey(cfg.sensorIdColumn),
    contentColumn: resolveKey(cfg.contentColumn),
    inZoneColumn: cfg.inZoneColumn ? resolveKey(cfg.inZoneColumn) : cfg.inZoneColumn,
  };
}

/** Config completa del carrusel: viz spec + chart.imageCarouselConfig. */
export function resolveImageCarouselConfig(chart, data) {
  if (!chart) return { ...DEFAULT_IMAGE_CAROUSEL };
  // vizFieldsToImageCarouselConfig siempre devuelve un valor para cada campo (el real o un
  // default), así que si lo aplicáramos sin condición pisaría la caché incluso cuando el chart
  // nunca pasó por el Editor de gráficos guiado. No alcanza con que exista "visualization": ese
  // objeto se usa para todo tipo de gráfico, así que un chart con visualization: {} (u otros
  // campos no relacionados al carrusel) no cuenta; hace falta al menos una clave "carousel*" real.
  const vizObject = chart.visualizationSpec?.visualization || {};
  const hasCarouselVizKeys = Object.keys(vizObject).some((k) => k.startsWith('carousel'));
  const fromViz = hasCarouselVizKeys ? vizFieldsToImageCarouselConfig(vizObject) : {};
  // Tres fuentes, de menor a mayor prioridad:
  // 1. chart.imageCarouselConfig: caché derivado que enrichChartImageConfig vuelve a escribir en
  //    cada render. Si ganara sobre cualquiera de las otras dos, un cambio recién guardado se
  //    pisaría con el valor viejo que ya quedó horneado ahí antes de ese guardado.
  // 2. chart.visualizationSpec.visualization (fromViz): lo que publica el Editor de gráficos
  //    guiado. El endpoint /api/chart-builder/publish no arma imageCarouselConfig (a diferencia
  //    de imageDensity/intervals/geoHeatmap), solo deja fresco el visualizationSpec, así que debe
  //    ganarle al caché.
  // 3. chart.advancedConfig.imageCarouselConfig: donde el Editor SQL (ChartEditDialog embebido)
  //    guarda los cambios del usuario directamente, sin pasar por visualizationSpec.
  const merged = {
    ...DEFAULT_IMAGE_CAROUSEL,
    ...(chart.imageCarouselConfig || {}),
    ...fromViz,
    ...(chart.advancedConfig?.imageCarouselConfig || {}),
  };
  // El editor guiado persiste recorte/filtro en visualizationSpec; el editor SQL solo en
  // advancedConfig. Si ambos existen, las claves presentes en el spec del builder deben ganar
  // (evita que un advancedConfig viejo en BD deje crop/filtro en false tras "Guardar cambios").
  if (Object.prototype.hasOwnProperty.call(vizObject, 'carouselCropMode')) {
    merged.cropMode = vizObject.carouselCropMode === true;
  }
  if (Object.prototype.hasOwnProperty.call(vizObject, 'carouselCropClassFilter')) {
    merged.cropClassFilter = vizObject.carouselCropClassFilter || '';
  }
  if (Object.prototype.hasOwnProperty.call(vizObject, 'carouselSingleImageMode')) {
    merged.singleImageMode = vizObject.carouselSingleImageMode === true;
  }
  if (Object.prototype.hasOwnProperty.call(vizObject, 'carouselShowOnlyInZone')) {
    merged.showOnlyInZone = vizObject.carouselShowOnlyInZone === true;
  }
  const rows = Array.isArray(data) ? data : [];
  const sample = rows.find((r) => r && typeof r === 'object') || null;
  return resolveImageCarouselColumnKeys(merged, sample);
}

export function imageCarouselVizFieldsFromConfig(cfg = {}) {
  return {
    carouselSensorTypeColumn: cfg.sensorTypeColumn || 'sensor_type',
    carouselSensorIdColumn: cfg.sensorIdColumn || 'sensor_id',
    carouselContentColumn: cfg.contentColumn || 'content',
    carouselSingleImageMode: cfg.singleImageMode === true,
    carouselInZoneColumn: cfg.inZoneColumn || '',
    carouselShowOnlyInZone: cfg.showOnlyInZone === true,
    carouselCropMode: cfg.cropMode === true,
    carouselCropClassFilter: cfg.cropClassFilter || '',
  };
}

function optionValuesHas(optionValues, key) {
  if (optionValues instanceof Set) return optionValues.has(key);
  if (Array.isArray(optionValues)) return optionValues.includes(key);
  return false;
}

/** Al cargar un gráfico: no guardar en UI el nombre lógico por defecto (equivale a automático). */
export function normalizeCarouselColumnFromStorage(stored, defaultLogical) {
  const v = String(stored || '').trim();
  if (!v || v === defaultLogical) return '';
  return v;
}

/**
 * Valor controlado para MUI Select del carrusel. '' = automático (MenuItem vacío).
 * Evita warnings cuando el spec trae sensor_type pero las opciones son alias del SQL builder.
 */
export function carouselColumnSelectValue(stored, defaultLogical, optionValues) {
  const v = String(stored || '').trim();
  if (!v || v === defaultLogical) return '';
  if (optionValuesHas(optionValues, v)) return v;
  return '';
}
